import gql from 'graphql-tag';
import React from 'react';
import { useQuery, useMutation, MutationFunction } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { Navigation, LayoutContent, Layout } from '../../components';
import { useStartAndEnd, useQueryParams } from '../../hooks';
import {
  OrdersListQuery,
  OrdersListQueryVariables,
  OrdersListQuery_orders_orders,
} from './__generated__/OrdersListQuery';
import { OrderRefund, OrderRefundVariables } from './__generated__/OrderRefund';
import { OrdersTable } from './components/OrdersTable';
import { flipDirection, SortDirection } from './utils';
import { RefundModal } from './RefundModal';
import { OrderFilter, OrderFilters } from './OrderFilter';

export type OrderQueryParams = {
  start?: string;
  end?: string;
  fulfillmentCenterId?: string;
  orderNumber?: string;
  orderId?: string;
  orderBy?: string;
  direction?: SortDirection;
  page?: string;
};

const headers = [
  'Reference ID',
  'Order Number',
  'Status',
  'Date',
  'Customer',
  'Type',
  'Fulfillment Center',
  'Items',
  'Total',
];

const orderByColumns = ['referencid', 'ordernumber', 'date', 'customer', 'type', 'fulfillmencenter', 'items'];

const ORDER_REFUND = gql`
  mutation OrderRefund($venueId: ID!, $orderId: ID!, $amount: Float) {
    orderRefund(venueId: $venueId, orderId: $orderId, amount: $amount)
  }
`;

const SESSION_QUERY = gql`
  query OrdersListQuery(
    $venueId: ID!
    $start: String!
    $end: String!
    $fulfillmentCenterId: String
    $orderNumber: String
    $orderId: String
    $page: Int!
  ) {
    venue(id: $venueId) {
      paymentProcessor
    }
    fulfillmentCenters(venueId: $venueId) {
      delivery
      id
      name
      pickup
      deliveryText
      menuId
      deliveryConfigurationId
      allowImmediateOrders
      isOpen
      isPaused
      advancedDeliverySetup
      disabled
    }
    orders(
      venueId: $venueId
      start: $start
      end: $end
      fulfillmentCenterId: $fulfillmentCenterId
      orderNumber: $orderNumber
      orderId: $orderId
      page: $page
    ) {
      perpage
      total
      orders {
        orderId
        orderNumber
        date
        customer
        isDelivery
        fulfillmentCenterName
        total
        status
        items {
          name
        }
      }
    }
  }
`;

interface OrderState {
  checked: any[];
  refundOrder: boolean;
  order: OrdersListQuery_orders_orders | null;
}

interface SessionRouterParams {
  venueId: string;
}

type SessionRouterProps = RouteComponentProps<SessionRouterParams>;

interface OrdersTableProps extends SessionRouterProps {
  page: number;
  orderBy: string;
  direction: SortDirection;
  data?: OrdersListQuery;
  error?: Error;
  loading: boolean;
  filters: OrderFilters;
  orderRefund: MutationFunction<OrderRefund, OrderRefundVariables>;
}

class SessionsUnwrapped extends React.Component<OrdersTableProps, OrderState> {
  public onChangeOrderNumber: any;
  public onChangeOrderId: any;

  constructor(props) {
    super(props);
    this.state = {
      checked: [],
      refundOrder: false,
      order: null,
    };
  }

  public updateUrl = (filters: OrderFilters, page: number, orderBy: string | null, direction: SortDirection | null) => {
    let req: OrderQueryParams = {
      page: (page ?? 1).toString(),
    };
    if (filters.fulfillmentCenterId) {
      req.fulfillmentCenterId = filters.fulfillmentCenterId;
    }

    if (filters.orderId) {
      req.orderId = filters.orderId;
    }

    if (filters.orderNumber) {
      req.orderNumber = filters.orderNumber;
    }

    if (filters.start && filters.end) {
      req.start = filters.start.toISOString();
      req.end = filters.end.toISOString();
    }

    if (orderBy) {
      req.orderBy = orderBy;
    }

    if (direction) {
      req.direction = direction;
    }

    const suffix = new URLSearchParams(req);
    this.props.history.push(`/venue/${this.props.match.params.venueId}/order?${suffix}`);
  };

  onUpdateFilters = (filters: OrderFilters) => {
    this.updateUrl(filters, this.props.page, this.props.orderBy, this.props.direction);
  };

  public goToNextPage = () => {
    this.updateUrl(this.props.filters, this.props.page + 1, this.props.orderBy, this.props.direction);
  };

  public goToPrevPage = () => {
    this.updateUrl(this.props.filters, this.props.page - 1, this.props.orderBy, this.props.direction);
  };

  public onClickHeaderCell = (index) => {
    const { orderBy, direction } = this.props;
    if (orderByColumns[index] === orderBy) {
      this.updateUrl(this.props.filters, this.props.page - 1, this.props.orderBy, flipDirection(direction));
    } else {
      this.updateUrl(this.props.filters, this.props.page - 1, orderByColumns[index], this.props.direction);
    }
  };

  public onPressCheckbox = (order) => {
    if (this.state.checked.includes(order.orderId)) {
      this.setState({
        checked: this.state.checked.filter((orderId) => orderId !== order.orderId),
      });
    } else {
      this.setState({
        checked: this.state.checked.concat(order.orderId),
      });
    }
  };

  public onClickRefund = async (order: OrdersListQuery_orders_orders) => {
    this.setState({
      refundOrder: true,
      order,
    });
  };

  closeModal = () => {
    this.setState({
      refundOrder: false,
    });
  };

  orderRefund = () => {
    const orderToRefund = this.state.order;
    this.setState({ refundOrder: false }, async () => {
      await this.props.orderRefund({
        variables: {
          venueId: this.props.match.params.venueId,
          orderId: orderToRefund.orderId,
          amount: orderToRefund.total,
        },
      });
    });
  };

  public onCheckAll = () => {
    if (this.state.checked.length === 0) {
      const checked = this.props.data.orders.orders.map((d) => d.orderId);
      this.setState({ checked });
    } else {
      this.setState({ checked: [] });
    }
  };

  public renderFooter = () => {
    return <div>Total Orders: {this.props.data.orders.total ?? 0}</div>;
  };

  onChangeField = (fieldName, e) => {
    this.setState({
      [fieldName]: e.target.value,
    } as any);
  };

  onReset = () => {
    this.props.history.push(`/venue/${this.props.match.params.venueId}/order`);
  };

  render() {
    const { data } = this.props;
    const orderByHeaderIndex = orderByColumns.indexOf(this.props.orderBy);
    const maxPage = (data && Math.ceil(data.orders.total / data.orders.perpage)) || 1;

    return (
      <Layout>
        <Navigation />
        <Layout.TopBar>
          <Layout.TopBar.Left>
            <Layout.TopBar.Title>Orders</Layout.TopBar.Title>
          </Layout.TopBar.Left>
        </Layout.TopBar>

        <LayoutContent error={this.props.error}>
          <div className='report-card'>
            {this.props.data?.fulfillmentCenters && (
              <OrderFilter
                values={this.props.filters}
                onUpdateFilters={this.onUpdateFilters}
                fulfillmentCenters={this.props.data.fulfillmentCenters}
              />
            )}
            <RefundModal
              toggle={this.closeModal}
              open={this.state.refundOrder === true}
              order={this.state.order}
              onRefund={this.orderRefund}
            />
            <OrdersTable
              headers={headers}
              loading={this.props.loading}
              rows={data ? data.orders.orders : undefined}
              onClickRefund={this.onClickRefund}
              page={this.props.page}
              maxPage={maxPage}
              goToPrevPage={this.goToPrevPage}
              goToNextPage={this.goToNextPage}
              FooterComponent={this.renderFooter}
              orderByHeaderIndex={orderByHeaderIndex}
              orderByDirection={this.props.direction}
              onClickHeaderCell={this.onClickHeaderCell}
            />
          </div>
        </LayoutContent>
      </Layout>
    );
  }
}

export const Orders: React.FC<SessionRouterProps> = (props) => {
  const { start, end } = useStartAndEnd();
  const queryParams = useQueryParams();

  const page = queryParams.get('page') ? Number(queryParams.get('page')) : 1;
  const orderBy = queryParams.get('orderBy') || 'date';
  const direction = (queryParams.get('direction') || 'desc') as SortDirection;
  const fulfillmentCenterId = queryParams.get('fulfillmentCenterId')
    ? queryParams.get('fulfillmentCenterId')
    : undefined;
  const orderNumber = queryParams.get('orderNumber') ? queryParams.get('orderNumber') : undefined;
  const orderId = queryParams.get('orderId') ? queryParams.get('orderId') : undefined;

  const { data, loading, error, refetch } = useQuery<OrdersListQuery, OrdersListQueryVariables>(SESSION_QUERY, {
    variables: {
      venueId: props.match.params.venueId,
      start: start.toISOString(),
      end: end.toISOString(),
      fulfillmentCenterId: fulfillmentCenterId,
      orderNumber: orderNumber,
      orderId: orderId,
      page: page,
    },
  });

  const [orderRefund, refundState] = useMutation<OrderRefund, OrderRefundVariables>(ORDER_REFUND, {
    onCompleted() {
      refetch();
    },
  });

  const filters: OrderFilters = React.useMemo(() => {
    return {
      start,
      end,
      fulfillmentCenterId,
      orderNumber,
      orderId,
    };
  }, [start, end, fulfillmentCenterId, orderNumber, orderId]);

  return (
    <SessionsUnwrapped
      {...props}
      loading={loading}
      error={error ?? refundState.error}
      data={data}
      page={page}
      filters={filters}
      orderBy={orderBy}
      direction={direction}
      orderRefund={orderRefund}
    />
  );
};
